import { App } from 'App'

import { FC, useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { toast } from 'react-toastify'
import { VerificationPage } from 'ufinet-web-components'
import {
	AuthContext,
	authService,
	AuthStatus,
	IAuthUser,
	ITokenData,
	AuthorityPattern,
	UserTypesEnum,
} from 'ufinet-web-functions'
import { PrivateRoutes } from './protected/PrivateRoutes'

const extractTokenFromUrl = (): ITokenData | null => {
	const urlParams = new URLSearchParams(window.location.search)
	const accessToken = urlParams.get('token')
	const refreshToken = urlParams.get('refreshToken')
	if (accessToken) {
		return { accessToken, refreshToken: refreshToken || '' }
	}
	return null
}

const cleanTokenFromUrl = () => {
	const url = new URL(window.location.href)
	url.searchParams.delete('token')
	url.searchParams.delete('refreshToken')
	window.history.replaceState({}, '', url.toString())
}

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
	try {
		const base64Url = token.split('.')[1]
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
				.join('')
		)
		return JSON.parse(jsonPayload)
	} catch {
		return null
	}
}

const fetchAuthorities = async (accessToken: string): Promise<string[]> => {
	const apiBaseUrl = process.env.REACT_APP_AUTHORITIES_API_BASE_URL
	const apiKey = process.env.REACT_APP_AUTHORITIES_API_KEY

	if (!apiBaseUrl || !apiKey) {
		console.warn('Authorities API env vars not configured, falling back to JWT roles')
		return []
	}

	try {
		const response = await fetch(`${apiBaseUrl}portalbackend/api/authorities`, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				apiKey,
			},
		})
		const data = await response.json()

		if (data?.authorities && Array.isArray(data.authorities) && data.authorities.length > 0) {
			return data.authorities
		}
	} catch (error) {
		console.warn('Failed to fetch authorities from API, falling back to JWT roles', error)
	}

	return []
}

const buildUserData = (accessToken: string, authorities: AuthorityPattern[]): IAuthUser | null => {
	const payload = decodeJwtPayload(accessToken)
	if (!payload) return null

	return {
		valid: true,
		expired: false,
		username: (payload.sub as string) || '',
		authorities,
		user: {
			fullName: (payload.fullname as string) || '',
			type: ((payload.usertype as string) || '') as UserTypesEnum,
		},
	}
}

const getAuthoritiesWithFallback = async (accessToken: string): Promise<AuthorityPattern[]> => {
	const apiAuthorities = await fetchAuthorities(accessToken)

	if (apiAuthorities.length > 0) {
		return apiAuthorities as AuthorityPattern[]
	}

	// Fallback: parse roles from JWT
	const payload = decodeJwtPayload(accessToken)
	if (payload && typeof payload.roles === 'string') {
		return payload.roles.split(',') as AuthorityPattern[]
	}

	return []
}

const redirectToPortalLogin = () => {
	const portalLoginUrl = process.env.REACT_APP_URL_PORTAL_LOGIN
	if (!portalLoginUrl) {
		console.error('REACT_APP_URL_PORTAL_LOGIN is not configured')
		return
	}
	const currentUrl = window.location.href
	window.location.href = `${portalLoginUrl}?fromLegacyUrl=${encodeURIComponent(currentUrl)}`
}

const AppRoutes: FC = () => {
	const intl = useIntl()
	const { status, setStatus, token, setToken, clear } = useContext(AuthContext)
	const hasInitialized = useRef(false)

	// On mount: if fresh tokens in URL, fetch authorities and log in; otherwise reuse existing session
	useEffect(() => {
		if (hasInitialized.current) return
		hasInitialized.current = true

		authService.setUpBroadcastChannel({ onLogoutReceived: clear })

		const urlToken = extractTokenFromUrl()

		if (urlToken) {
			// Fresh login from portal: save tokens, fetch authorities, and set logged in
			cleanTokenFromUrl()
			setToken(urlToken)
			;(async () => {
				const authorities = await getAuthoritiesWithFallback(urlToken.accessToken)
				const userData = buildUserData(urlToken.accessToken, authorities)

				if (!userData) {
					redirectToPortalLogin()
					return
				}

				setStatus(AuthStatus.LOGGED_IN, userData)
			})()
		} else if (token?.accessToken && status === AuthStatus.LOGGED_IN) {
			// Existing session from localStorage: keep current userData and authorities as-is
		} else {
			// No token anywhere: redirect to portal login
			redirectToPortalLogin()
		}
	}, [])

	// Handle logout triggered by FetchAPI 401/403 or BroadcastChannel
	useEffect(() => {
		if (hasInitialized.current && status === AuthStatus.LOGGED_OUT) {
			redirectToPortalLogin()
		}
	}, [status])

	// Periodically refresh token
	const refreshToken = useCallback(async () => {
		if (status !== AuthStatus.LOGGED_IN || !token?.accessToken || !token?.refreshToken) return

		const apiBaseUrl = process.env.REACT_APP_AUTHORITIES_API_BASE_URL
		const apiKey = process.env.REACT_APP_AUTHORITIES_API_KEY

		if (!apiBaseUrl || !apiKey) return

		try {
			const response = await fetch(`${apiBaseUrl}portalbackend/api/auth/token/refresh`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token.accessToken}`,
					apiKey,
				},
				body: JSON.stringify({ refreshToken: token.refreshToken }),
			})

			if (!response.ok) throw new Error('Refresh failed')

			const data = await response.json()
			setToken({ accessToken: data.accessToken, refreshToken: token.refreshToken })
		} catch {
			toast.dismiss()
			toast.error(intl.formatMessage({ id: 'AUTH.SESSION.EXPIRED' }))
			redirectToPortalLogin()
		}
	}, [status, token, setToken, intl])

	useEffect(() => {
		const refreshInterval = setInterval(refreshToken, 15 * 60 * 1000)

		return () => {
			clearInterval(refreshInterval)
		}
	}, [refreshToken])

	return (
		<BrowserRouter>
			<Routes>
				<Route element={<App />}>
					{status === AuthStatus.LOGGED_IN ? (
						<>
							<Route path="/*" element={<PrivateRoutes />} />
						</>
					) : (
						<>
							<Route path="/*" element={<VerificationPage />} />
						</>
					)}
				</Route>
			</Routes>
		</BrowserRouter>
	)
}

export { AppRoutes }
